import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { Partner, PartnerType } from "../../../Partner/Dto/PartnerModel";
import { TeamLeaderListItem } from "../../../TeamLeader/Dto/TeamLeaderModel";
import {
  ShipmentDestinationSectionState,
  ShipmentDestinationSeed,
  ShipmentDestinationSnapshot,
} from "../../Dto/ShipmentDestinationFormModel";

export function createEmptyShipmentAddress(): LocationAddress {
  return {
    stateProvinceRegion: "",
    countyDistrict: "",
    subdistrictMunicipality: "",
    communeVillage: "",
    line: "",
    zip: "",
    longitude: 0,
    latitude: 0,
  };
}

export function cloneShipmentAddress(address?: LocationAddress | null): LocationAddress {
  return {
    ...createEmptyShipmentAddress(),
    ...(address ?? {}),
  };
}

export function isShipmentAddressEmpty(address?: LocationAddress | null): boolean {
  return ![
    address?.stateProvinceRegion,
    address?.countyDistrict,
    address?.subdistrictMunicipality,
    address?.communeVillage,
    address?.line,
    address?.zip,
  ].some((value) => value?.trim());
}

export function formatShipmentAddress(address?: LocationAddress | null): string {
  if (!address) {
    return "";
  }

  return [
    address.line,
    address.communeVillage,
    address.subdistrictMunicipality,
    address.countyDistrict,
    address.stateProvinceRegion,
    address.zip,
  ]
    .filter((item) => !!item)
    .join(", ");
}

export function createSeededDestinationSection(
  seed: ShipmentDestinationSeed,
  options: {
    addressSource: "partner" | "recipient" | "snapshot";
    phoneSource: "recipient" | "snapshot";
  },
): ShipmentDestinationSectionState {
  const selectedPartner = (() => {
    if (seed.partnerId && seed.partnerName) {
      return {
        id: seed.partnerId,
        name: seed.partnerName,
        type: PartnerType.Other,
        phone: seed.partnerPhone ?? undefined,
        address: cloneShipmentAddress(seed.partnerAddress),
        createdAt: "",
        updatedAt: "",
      } satisfies Partner;
    }
    return null;
  })();

  const selectedRecipient = (() => {
    if (seed.recipientId && seed.recipientName) {
      return {
        id: seed.recipientId,
        name: seed.recipientName,
        phone: seed.recipientPhone ?? undefined,
        email: null,
        address: cloneShipmentAddress(seed.recipientAddress),
        partners: (() => {
          if (selectedPartner && seed.partnerId) {
            return [
              {
                id: seed.partnerId,
                name: selectedPartner.name,
                address: selectedPartner.address,
                phone: selectedPartner.phone,
              },
            ];
          }
          return [];
        })(),
        createdAt: "",
        updatedAt: "",
      } satisfies TeamLeaderListItem;
    }
    return null;
  })();

  const addressSeed = (() => {
    if (options.addressSource === "recipient") {
      return seed.recipientAddress;
    }
    return (() => {
      if (options.addressSource === "partner") {
        return seed.partnerAddress;
      }
      return seed.snapshotAddress;
    })();
  })();

  const phoneSeed = (() => {
    if (options.phoneSource === "recipient") {
      return seed.recipientPhone;
    }
    return seed.snapshotPhone;
  })();

  return {
    selectedPartnerId: seed.partnerId ?? "",
    selectedPartner,
    selectedRecipientId: seed.recipientId ?? "",
    selectedRecipient,
    phoneValue: phoneSeed ?? "",
    addressValue: cloneShipmentAddress(addressSeed),
    recipientsAvailable: (() => {
      if (seed.recipientId) {
        return true;
      }
      return null;
    })(),
  };
}

export function createEmptyDestinationSection(): ShipmentDestinationSectionState {
  return {
    selectedPartnerId: "",
    selectedPartner: null,
    selectedRecipientId: "",
    selectedRecipient: null,
    phoneValue: "",
    addressValue: createEmptyShipmentAddress(),
    recipientsAvailable: null,
  };
}

export function buildShipmentDestinationSnapshot(
  state: Pick<ShipmentDestinationSectionState, "selectedPartner" | "selectedRecipient" | "phoneValue" | "addressValue">,
): ShipmentDestinationSnapshot | null {
  const partnerName = state.selectedPartner?.name?.trim() ?? "";
  const recipientName = state.selectedRecipient?.name?.trim() ?? "";

  if (!partnerName) {
    return null;
  }

  return {
    partnerName,
    recipientName,
    recipientPhone: state.phoneValue,
    destinationAddress: cloneShipmentAddress(state.addressValue),
  };
}
